import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { Row, Col, Card, Statistic, Empty, Spin, Segmented, Table, Button, Typography, Space, Select, Checkbox, DatePicker, Tooltip as AntTooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, ComposedChart, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import FilterPanel from '../components/FilterPanel';
import api from '../api';

const HIDDEN_FILTERS = new Set([
  'direction', 'media_name', 'agent_name',
  'contact_id', 'disp_name', 'contact_type',
  'contact_end_reason', 'ani_called_back',
  'hold_time', 'agent_time', 'acw_time', 'active_talk_time', 'flagged',
]);

function fmt(v, decimals = 0) {
  if (v == null || isNaN(v)) return '—';
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtMmSs(secs) {
  if (secs == null || isNaN(secs)) return '—';
  const total = Math.round(Number(secs));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtMonth(key) {
  if (!key) return '';
  const [year, month] = key.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(month, 10) - 1]} ${year}`;
}

function KpiCard({ title, value, suffix = '', decimals = 0, placeholder = false, mmss = false }) {
  const display = placeholder ? '—' : mmss ? fmtMmSs(value) : fmt(value, decimals);
  const sfx     = (!placeholder && value != null && !mmss) ? suffix : '';
  return (
    <Card size="small" styles={{ body: { padding: '12px 16px', textAlign: 'center' } }}>
      <Statistic title={title} value={display} suffix={sfx} formatter={(v) => v} />
    </Card>
  );
}

const PCT_LABEL = ({ x, y, width, value }) => {
  if (value == null) return null;
  return (
    <text x={x + width / 2} y={y - 4} fill="#555" textAnchor="middle" fontSize={10}>
      {fmt(value, 1)}%
    </text>
  );
};

// ── Shared table columns for all period views ─────────────────────────────
function makeCols(periodTitle) {
  return [
    { title: periodTitle, dataIndex: 'Label', key: 'Label', fixed: 'left', width: 130 },
    { title: 'Customer SLA', dataIndex: 'CustomerSLA', key: 'CustomerSLA', width: 110, align: 'right',
      render: (v) => v != null ? `${fmt(v, 1)}%` : '—' },
    { title: 'Raw SLA', dataIndex: 'RawSLA', key: 'RawSLA', width: 90, align: 'right',
      render: (v) => v != null ? `${fmt(v, 1)}%` : '—' },
    { title: 'Avg Offered/Day', dataIndex: 'AvgOfferedPerDay', key: 'AvgOfferedPerDay', width: 125, align: 'right',
      render: (v) => v != null ? fmt(v, 0) : '—' },
    { title: 'Offered', dataIndex: 'Offered', key: 'Offered', width: 90, align: 'right',
      render: (v) => v != null ? Number(v).toLocaleString() : '—' },
    { title: 'Handled', dataIndex: 'Handled', key: 'Handled', width: 90, align: 'right',
      render: (v) => v != null ? Number(v).toLocaleString() : '—' },
    { title: 'Handled As Callback', dataIndex: 'HandledAsCallback', key: 'HandledAsCallback', width: 145, align: 'right',
      render: (v) => v != null ? Number(v).toLocaleString() : '—' },
    { title: 'Abandon', dataIndex: 'Abandon', key: 'Abandon', width: 90, align: 'right',
      render: (v) => v != null ? Number(v).toLocaleString() : '—' },
    { title: 'Raw Abandon Rate', dataIndex: 'RawAbandonRate', key: 'RawAbandonRate', width: 135, align: 'right',
      render: (v) => v != null ? `${fmt(v, 1)}%` : '—' },
    { title: 'Transferred VM', dataIndex: 'TransferredVM', key: 'TransferredVM', width: 120, align: 'right',
      render: (v) => v != null ? Number(v).toLocaleString() : '—' },
    { title: 'Raw ASA', dataIndex: 'RawASA', key: 'RawASA', width: 90, align: 'right',
      render: (v) => fmtMmSs(v) },
    { title: 'Raw AHT', dataIndex: 'RawAHT', key: 'RawAHT', width: 90, align: 'right',
      render: (v) => fmtMmSs(v) },
    { title: 'Raw AvgTalk', dataIndex: 'RawAvgTalk', key: 'RawAvgTalk', width: 100, align: 'right',
      render: (v) => fmtMmSs(v) },
    { title: 'Raw ACW', dataIndex: 'RawACW', key: 'RawACW', width: 90, align: 'right',
      render: (v) => fmtMmSs(v) },
    { title: 'Abandon InSLA', dataIndex: 'AbandonInSLA', key: 'AbandonInSLA', width: 115, align: 'right',
      render: (v) => v != null ? Number(v).toLocaleString() : '—' },
  ];
}

// ── Excel export ──────────────────────────────────────────────────────────
function exportPeriodView(data, summary, periodTitle) {
  const COLS = [
    { header: periodTitle,              get: (r) => r.Label },
    { header: 'Customer SLA (%)',       get: (r) => r.CustomerSLA     != null ? +Number(r.CustomerSLA).toFixed(1)     : null },
    { header: 'Raw SLA (%)',            get: (r) => r.RawSLA          != null ? +Number(r.RawSLA).toFixed(1)          : null },
    { header: 'Avg Offered/Day',        get: (r) => r.AvgOfferedPerDay != null ? +Number(r.AvgOfferedPerDay).toFixed(0) : null },
    { header: 'Offered',                get: (r) => r.Offered          != null ? Number(r.Offered)          : null },
    { header: 'Handled',                get: (r) => r.Handled          != null ? Number(r.Handled)          : null },
    { header: 'Handled As Callback',    get: (r) => r.HandledAsCallback != null ? Number(r.HandledAsCallback) : null },
    { header: 'Abandon',                get: (r) => r.Abandon          != null ? Number(r.Abandon)          : null },
    { header: 'Raw Abandon Rate (%)',   get: (r) => r.RawAbandonRate   != null ? +Number(r.RawAbandonRate).toFixed(1)   : null },
    { header: 'Transferred VM',         get: (r) => r.TransferredVM    != null ? Number(r.TransferredVM)    : null },
    { header: 'Raw ASA',                get: (r) => fmtMmSs(r.RawASA) },
    { header: 'Raw AHT',                get: (r) => fmtMmSs(r.RawAHT) },
    { header: 'Raw AvgTalk',            get: (r) => fmtMmSs(r.RawAvgTalk) },
    { header: 'Raw ACW',                get: (r) => fmtMmSs(r.RawACW) },
    { header: 'Abandon InSLA',          get: (r) => r.AbandonInSLA     != null ? Number(r.AbandonInSLA)     : null },
  ];

  const rows = data.map((r) => COLS.map((c) => c.get(r)));
  if (summary) {
    rows.push(COLS.map((c) => c.get({ ...summary, Label: 'Grand Total' })));
  }

  const ws = XLSX.utils.aoa_to_sheet([COLS.map((c) => c.header), ...rows]);

  // Auto-width columns
  const colWidths = COLS.map((c) => ({ wch: Math.max(c.header.length, 12) }));
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, periodTitle.replace(/[/\\?*[\]:]/g, '_').slice(0, 31));
  XLSX.writeFile(wb, `campaign-stats-${periodTitle.toLowerCase().replace(/\s+/g, '-')}.xlsx`);
}

// ── Reusable table + 3 charts for any period grouping ────────────────────
// By Day of Week, under its table: one chart per weekday (Monday first), each
// date of that weekday in the range -- Offered as the bar, Raw SLA printed
// above it (one axis; both are in the tooltip).
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DOW_NAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SLA_ABOVE = ({ x, y, width, index, data }) => {
  const v = data[index]?.RawSLA;
  if (v == null) return null;
  return (
    <text x={x + width / 2} y={y - 4} fill="#13c2c2" textAnchor="middle" fontSize={10} fontWeight={600}>
      {fmt(v, 1)}%
    </text>
  );
};
function DowDatesCharts({ daily }) {
  const byDow = {};
  for (const r of daily || []) {
    const d = dayjs(r.Day);
    if (!d.isValid()) continue;
    (byDow[d.day()] = byDow[d.day()] || []).push(r);
  }
  const days = DOW_ORDER.filter((d) => byDow[d]?.length);
  if (!days.length) return null;
  return (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      {days.map((d) => {
        const rows = [...byDow[d]].sort((a, b) => String(a.Day).localeCompare(String(b.Day)));
        return (
          <Col xs={24} lg={12} xxl={8} key={d}>
            <Card size="small" title={`${DOW_NAME[d]}: Offered and SLA`}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={rows} margin={{ top: 20, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="Day" tick={{ fontSize: 10 }} tickFormatter={(v) => dayjs(v).format('MM/DD')} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(v) => dayjs(v).format('ddd MMM D, YYYY')}
                    formatter={(v, _n, { payload }) => [
                      `${(v ?? 0).toLocaleString()} offered, SLA ${payload.RawSLA != null ? `${fmt(payload.RawSLA, 1)}%` : '—'}`, 'Calls']}
                  />
                  <Bar dataKey="Offered" fill="#1677ff" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="Offered" position="insideTop" style={{ fontSize: 10, fill: '#fff' }}
                      formatter={(v) => (v ? v.toLocaleString() : '')} />
                    <LabelList content={<SLA_ABOVE data={rows} />} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}

function PeriodView({ data, periodTitle, summary, reverseForChart = false }) {
  const cols = useMemo(() => makeCols(periodTitle), [periodTitle]);

  const mc = (reverseForChart ? [...data].reverse() : data).map((r) => ({
    ...r,
    RawAHTMin:     r.RawAHT     != null ? +(r.RawAHT     / 60).toFixed(2) : null,
    RawAvgTalkMin: r.RawAvgTalk != null ? +(r.RawAvgTalk / 60).toFixed(2) : null,
    RawACWMin:     r.RawACW     != null ? +(r.RawACW     / 60).toFixed(2) : null,
  }));

  const cell = (content, idx) => (
    <Table.Summary.Cell index={idx} align="right" style={{ fontWeight: 600 }}>{content}</Table.Summary.Cell>
  );

  return (
    <>
      <div style={{ marginBottom: 8, textAlign: 'right' }}>
        <Button
          icon={<DownloadOutlined />}
          size="small"
          disabled={data.length === 0}
          onClick={() => exportPeriodView(data, summary, periodTitle)}
        >
          Export to Excel
        </Button>
      </div>
      <Table
        dataSource={data}
        columns={cols}
        rowKey="Label"
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={false}
        summary={() => {
          if (!summary || data.length === 0) return null;
          const s = summary;
          return (
            <Table.Summary.Row style={{ fontWeight: 600 }}>
              <Table.Summary.Cell index={0} style={{ fontWeight: 600 }}>Grand Total</Table.Summary.Cell>
              {cell(s.CustomerSLA != null ? `${fmt(s.CustomerSLA, 1)}%` : '—', 1)}
              {cell(s.RawSLA != null ? `${fmt(s.RawSLA, 1)}%` : '—', 2)}
              {cell(s.AvgOfferedPerDay != null ? fmt(s.AvgOfferedPerDay, 0) : '—', 3)}
              {cell(s.Offered != null ? Number(s.Offered).toLocaleString() : '—', 4)}
              {cell(s.Handled != null ? Number(s.Handled).toLocaleString() : '—', 5)}
              {cell(s.HandledAsCallback != null ? Number(s.HandledAsCallback).toLocaleString() : '—', 6)}
              {cell(s.Abandon != null ? Number(s.Abandon).toLocaleString() : '—', 7)}
              {cell(s.RawAbandonRate != null ? `${fmt(s.RawAbandonRate, 1)}%` : '—', 8)}
              {cell(s.TransferredVM != null ? Number(s.TransferredVM).toLocaleString() : '—', 9)}
              {cell(fmtMmSs(s.RawASA), 10)}
              {cell(fmtMmSs(s.RawAHT), 11)}
              {cell(fmtMmSs(s.RawAvgTalk), 12)}
              {cell(fmtMmSs(s.RawACW), 13)}
              {cell(s.AbandonInSLA != null ? Number(s.AbandonInSLA).toLocaleString() : '—', 14)}
            </Table.Summary.Row>
          );
        }}
      />

      {mc.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card title="Offered">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={mc} margin={{ top: 24, right: 24, bottom: 8, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, name) => [v != null ? v.toLocaleString() : '—', name]} />
                  <Legend />
                  <Line type="monotone" dataKey="Offered" name="Offered" stroke="#1677ff" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#1677ff', formatter: (v) => v != null ? v.toLocaleString() : '' }} />
                  <Line type="monotone" dataKey="Handled" name="Handled" stroke="#13c2c2" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#13c2c2', formatter: (v) => v != null ? v.toLocaleString() : '' }} />
                  <Line type="monotone" dataKey="Abandon" name="Abandon" stroke="#eb2f96" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#eb2f96', formatter: (v) => v != null ? v.toLocaleString() : '' }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          <Col xs={24}>
            <Card title="HT">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={mc} margin={{ top: 24, right: 24, bottom: 8, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, name) => [v != null ? `${v} min` : '—', name]} />
                  <Legend />
                  <Line type="monotone" dataKey="RawAHTMin" name="Raw AHT" stroke="#1677ff" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#1677ff', formatter: (v) => v != null ? v : '' }} />
                  <Line type="monotone" dataKey="RawAvgTalkMin" name="Raw AvgTalk" stroke="#13c2c2" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#13c2c2', formatter: (v) => v != null ? v : '' }} />
                  <Line type="monotone" dataKey="RawACWMin" name="Raw ACW" stroke="#eb2f96" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#eb2f96', formatter: (v) => v != null ? v : '' }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          <Col xs={24}>
            <Card title="SLA">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={mc} margin={{ top: 24, right: 24, bottom: 8, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, name) => [v != null ? `${fmt(v, 1)}%` : '—', name]} />
                  <Legend />
                  <Line type="monotone" dataKey="CustomerSLA" name="Customer SLA" stroke="#1677ff" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#1677ff', formatter: (v) => v != null ? `${fmt(v, 1)}%` : '' }} />
                  <Line type="monotone" dataKey="RawSLA" name="Raw SLA" stroke="#13c2c2" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#13c2c2', formatter: (v) => v != null ? `${fmt(v, 1)}%` : '' }} />
                  <Line type="monotone" dataKey="RawAbandonRate" name="Raw Abandon Rate" stroke="#eb2f96" strokeWidth={2}
                    label={{ fontSize: 10, fill: '#eb2f96', formatter: (v) => v != null ? `${fmt(v, 1)}%` : '' }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}

// ── Single Date tab helpers (mirrors CampaignStatsTodayPage By Hour tab) ─────

const LOOKBACK_OPTIONS = [1, 2, 3, 4, 6, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52].map(
  (n) => ({ value: n, label: String(n) })
);

const SD_COLORS = [
  '#4096ff', '#fa8c16', '#13c2c2', '#722ed1', '#eb2f96',
  '#52c41a', '#fadb14', '#f5222d', '#2f54eb', '#d4380d',
];
const sdColor = (i) => SD_COLORS[i % SD_COLORS.length];

function fmtHour(h) {
  if (h === 0)  return '12AM';
  if (h < 12)  return `${h}AM`;
  if (h === 12) return '12PM';
  return `${h - 12}PM`;
}

function buildHourChartData(rows, valueField, suffix) {
  if (!rows.length) return { data: [], campaigns: [] };
  const hours     = [...new Set(rows.map((r) => r.Hour))].sort((a, b) => a - b);
  const campaigns = [...new Set(rows.map((r) => r.Campaign))].sort();
  const data = hours.map((h) => {
    const rec      = { hour: h, label: fmtHour(h) };
    const hourRows = rows.filter((r) => r.Hour === h);
    campaigns.forEach((c) => {
      const match = hourRows.find((r) => r.Campaign === c);
      rec[`${c}_${suffix}`] = match != null ? (match[valueField] ?? null) : null;
    });
    return rec;
  });
  return { data, campaigns };
}

function buildSlaHourChartData(rows) {
  if (!rows.length) return { data: [], campaigns: [] };
  const hours     = [...new Set(rows.map((r) => r.Hour))].sort((a, b) => a - b);
  const campaigns = [...new Set(rows.map((r) => r.Campaign))].sort();
  const data = hours.map((h) => {
    const rec      = { hour: h, label: fmtHour(h) };
    const hourRows = rows.filter((r) => r.Hour === h);
    campaigns.forEach((c) => {
      const match = hourRows.find((r) => r.Campaign === c);
      rec[`${c}_raw`] = match?.RawSLA != null ? +Number(match.RawSLA).toFixed(1) : null;
    });
    return rec;
  });
  return { data, campaigns };
}

function buildHourPivotGroups(rows, keyFn, metaFn) {
  if (!rows.length) return { pivotRows: [], activeHours: [], campaignSpans: {} };
  const activeHours = [...new Set(rows.map((r) => r.Hour))].sort((a, b) => a - b);
  const lookup = {};
  rows.forEach((r) => {
    const k = keyFn(r);
    if (!lookup[k]) lookup[k] = metaFn(r);
    lookup[k][r.Hour] = r.RecordCount;
  });
  const pivotRows = Object.values(lookup);
  pivotRows.sort((a, b) => {
    if (a._campaign !== b._campaign) return a._campaign.localeCompare(b._campaign);
    const tA = activeHours.reduce((s, h) => s + (a[h] || 0), 0);
    const tB = activeHours.reduce((s, h) => s + (b[h] || 0), 0);
    return tB - tA;
  });
  const campaignSpans = {};
  pivotRows.forEach((r, i) => {
    if (!campaignSpans[r._campaign]) campaignSpans[r._campaign] = { start: i, span: 0 };
    campaignSpans[r._campaign].span++;
  });
  return { pivotRows, activeHours, campaignSpans };
}

function SDHourBarChart({ title, data, campaigns, metricKey, labelFmt, yFmt, hasAvg, avgLabel, alertHigher, alertLower, totalsOverride }) {
  if (!data.length) return null;
  const chartW = Math.max(700, data.length * Math.max(55, campaigns.length * 22 + 12));

  const totals = totalsOverride ?? (labelFmt ? [] : campaigns.map((c, i) => {
    const vals = data.map((row) => row[`${c}_${metricKey}`]).filter((v) => v != null);
    if (!vals.length) return { c, i, display: '—' };
    const sum = vals.reduce((a, b) => a + b, 0);
    return { c, i, display: sum.toLocaleString() };
  }));

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <Typography.Text strong>{title}</Typography.Text>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {totals.map(({ c, i, display }) => (
            <span key={c} style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4, background: '#f5f5f5', border: `1px solid ${sdColor(i)}55`, color: '#595959' }}>
              <span style={{ color: sdColor(i), fontWeight: 600 }}>{c}</span>
              {': '}{display}
            </span>
          ))}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ width: chartW }}>
          <ResponsiveContainer width="100%" height={270}>
            <ComposedChart data={data} margin={{ top: 22, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={yFmt || undefined} width={yFmt ? 70 : 40} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => [labelFmt ? labelFmt(v) : (v != null ? Math.round(v * 10) / 10 : v), name]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {campaigns.map((c, i) => {
                const baseColor = sdColor(i);
                const key = `${c}_${metricKey}`;
                return (
                  <Bar key={c} dataKey={key} name={c} fill={baseColor} maxBarSize={36}>
                    {(alertHigher || alertLower) && hasAvg && data.map((entry, idx) => {
                      const val = entry[key];
                      const avg = entry._dow_avg;
                      const isAlert = val != null && avg != null
                        && (alertHigher ? val > avg : val < avg);
                      return <Cell key={idx} fill={isAlert ? '#ff4d4f' : baseColor} />;
                    })}
                    <LabelList
                      dataKey={key}
                      position="top"
                      style={{ fontSize: 9, fill: baseColor }}
                      formatter={(v) => (v != null ? (labelFmt ? labelFmt(v) : v) : '')}
                    />
                  </Bar>
                );
              })}
              {hasAvg && (
                <Line
                  type="monotone"
                  dataKey="_dow_avg"
                  name={avgLabel || 'DOW Avg'}
                  stroke="#595959"
                  strokeDasharray="5 3"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function SDHourPivotTable({ title, pivotRows, activeHours, campaignSpans, fixedCols, loading }) {
  const fixedCount = 1 + fixedCols.length;
  const campCol = useMemo(() => ({
    title: 'Campaign Name', dataIndex: '_campaign', fixed: 'left', width: 130, ellipsis: true,
    onCell: (record, rowIndex) => {
      const info = campaignSpans[record._campaign];
      if (!info) return {};
      return rowIndex === info.start ? { rowSpan: info.span } : { rowSpan: 0 };
    },
  }), [campaignSpans]);
  const hourCols = useMemo(() => activeHours.map((h) => ({
    title: fmtHour(h), key: `h${h}`, width: 60, align: 'right',
    render: (_, r) => { const v = r[h]; return (v != null && v > 0) ? v : '-'; },
  })), [activeHours]);
  const totalCol = useMemo(() => ({
    title: 'Grand total', key: '_total', width: 90, align: 'right',
    render: (_, r) => activeHours.reduce((s, h) => s + (r[h] || 0), 0) || '-',
  }), [activeHours]);
  const columns = [campCol, ...fixedCols, ...hourCols, totalCol];
  return (
    <div style={{ marginBottom: 28 }}>
      {title && <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>{title}</Typography.Text>}
      <Table
        rowKey={(_, i) => i}
        dataSource={pivotRows}
        columns={columns}
        size="small"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={false}
        locale={{ emptyText: loading ? ' ' : 'No data' }}
        summary={(pageData) => {
          const totals = {};
          activeHours.forEach((h) => { totals[h] = pageData.reduce((s, r) => s + (r[h] || 0), 0); });
          const grand = Object.values(totals).reduce((s, v) => s + v, 0);
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={fixedCount}>Grand total</Table.Summary.Cell>
                {activeHours.map((h, i) => (
                  <Table.Summary.Cell key={h} index={i + 1} align="right">{totals[h] || '-'}</Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={activeHours.length + 1} align="right">{grand || '-'}</Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </div>
  );
}

// ── Date range groups ─────────────────────────────────────────────────────
// Summary, By Week, By Day of Week, By Day, By Hour all share one date range
// so switching between them preserves whatever the user set.
const SHARED_RANGE_VIEWS  = new Set(['summary', 'by-week', 'by-dow', 'by-day', 'by-hour']);
const SHARED_DEFAULT_RANGE = () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'day')];
const MONTHLY_DEFAULT_RANGE = () => [dayjs().subtract(12, 'month').startOf('month'), dayjs().subtract(1, 'day')];

/**
 * `embedded` renders this page's content without its own filter panel or
 * title, driven by `filters` supplied from outside. The Campaign Summary uses
 * it so the campaign's tab and this page cannot disagree: there is one
 * implementation of these numbers, not two.
 */
export default function CampaignStatsPage({ embedded = false, filters: embeddedFilters = null }) {
  const filterRef         = useRef(null);
  const viewRef           = useRef('summary');
  const lastFiltersRef    = useRef({});

  const [view,    setView]    = useState('summary');
  const [summary, setSummary] = useState(null);
  const [daily,   setDaily]   = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [byWeek,  setByWeek]  = useState([]);
  const [byDow,   setByDow]   = useState([]);
  const [byHour,  setByHour]  = useState([]);
  const [loading, setLoading] = useState(false);

  const sdLookbackWeeksRef = useRef(6);

  const [singleDateData,  setSingleDateData]  = useState(null);
  const [sdLoading,       setSdLoading]       = useState(false);
  const [sdLookbackWeeks, setSdLookbackWeeks] = useState(6);
  const [sdHideAvg,       setSdHideAvg]       = useState(false);
  const [sdLookbackData,  setSdLookbackData]  = useState(null);

  const fetchSingleDateLookback = useCallback((filters, weeks) => {
    const campaign = filters.campaign;
    const date     = filters.start_date;
    if (!campaign || !date) { setSdLookbackData(null); return; }
    api.get('/api/campaign-stats/single-date-lookback', {
      params: { campaign, date, weeks },
    })
      .then(r => { setSdLookbackData(Array.isArray(r.data) ? r.data : null); })
      .catch(() => { setSdLookbackData(null); });
  }, []);

  const fetchSingleDate = useCallback((filters) => {
    setSdLoading(true);
    setSingleDateData(null);
    setSdLookbackData(null);
    api.get('/api/campaign-stats/single-date', { params: filters })
      .then(r => { setSingleDateData(r.data); })
      .catch(() => {})
      .finally(() => setSdLoading(false));
    fetchSingleDateLookback(filters, sdLookbackWeeksRef.current);
  }, [fetchSingleDateLookback]);

  const handleSdLookbackWeeksChange = useCallback((weeks) => {
    setSdLookbackWeeks(weeks);
    sdLookbackWeeksRef.current = weeks;
    fetchSingleDateLookback(lastFiltersRef.current, weeks);
  }, [fetchSingleDateLookback]);

  const handleViewChange = useCallback((newView) => {
    const prevView = viewRef.current;
    setView(newView);
    viewRef.current = newView;
    if (newView === 'single-date') {
      filterRef.current?.setDateRange(dayjs().subtract(1, 'day'));
    } else if (newView === 'monthly') {
      filterRef.current?.setDateRange(MONTHLY_DEFAULT_RANGE());
      setTimeout(() => filterRef.current?.triggerSearch(), 0);
    } else {
      if (!SHARED_RANGE_VIEWS.has(prevView)) {
        filterRef.current?.setDateRange(SHARED_DEFAULT_RANGE());
      }
      setTimeout(() => filterRef.current?.triggerSearch(), 0);
    }
  }, []);

  const handleSearch = useCallback((filters) => {
    lastFiltersRef.current = filters;
    if (viewRef.current === 'single-date') {
      fetchSingleDate(filters);
      return;
    }
    setLoading(true);
    setSummary(null);
    setDaily([]); setMonthly([]); setByWeek([]); setByDow([]); setByHour([]);
    Promise.all([
      api.get('/api/campaign-stats/summary',  { params: filters }),
      api.get('/api/campaign-stats/daily',    { params: filters }),
      api.get('/api/campaign-stats/monthly',  { params: filters }),
      api.get('/api/campaign-stats/by-week',  { params: filters }),
      api.get('/api/campaign-stats/by-dow',   { params: filters }),
      api.get('/api/campaign-stats/by-hour',  { params: filters }),
    ])
      .then(([s, d, m, w, dow, h]) => {
        setSummary(s.data);
        setDaily(  Array.isArray(d.data)   ? d.data   : []);
        setMonthly(Array.isArray(m.data)   ? m.data   : []);
        setByWeek( Array.isArray(w.data)   ? w.data   : []);
        setByDow(  Array.isArray(dow.data) ? dow.data : []);
        setByHour( Array.isArray(h.data)   ? h.data   : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchSingleDate]);

  const s = summary || {};

  // Daily chart data (for Summary view bar charts)
  const dailyChart = [...daily].reverse().map((r) => ({
    ...r,
    AbandonRatePct: r.RawAbandonRate != null ? +Number(r.RawAbandonRate).toFixed(1) : null,
    RawSLAPct:      r.RawSLA         != null ? +Number(r.RawSLA).toFixed(1)         : null,
  }));

  // Pre-labeled datasets — each row gets a Label field; table order = DESC (newest first)
  const monthlyData = useMemo(() =>
    monthly.map((r) => ({ ...r, Label: fmtMonth(r.MonthKey) })), [monthly]);

  const byWeekData = useMemo(() =>
    byWeek.map((r) => ({ ...r, Label: dayjs(r.WeekStart).format('MMM D, YYYY') })), [byWeek]);

  const byDowData = useMemo(() =>
    byDow.map((r) => ({ ...r, Label: r.DayName })), [byDow]);

  const dailyData = useMemo(() =>
    daily.map((r) => ({ ...r, Label: r.Day })), [daily]);

  const byHourData = useMemo(() =>
    byHour.map((r) => ({ ...r, Label: `${String(r.Hour).padStart(2, '0')}:xx` })), [byHour]);

  const { data: sdOfferedData, campaigns: sdOfferedCampaigns } = useMemo(
    () => buildHourChartData(singleDateData?.offered_by_hour ?? [], 'RecordCount', 'offered'),
    [singleDateData]);
  const { data: sdAbandonData, campaigns: sdAbandonCampaigns } = useMemo(
    () => buildHourChartData(singleDateData?.abandon_by_hour ?? [], 'RecordCount', 'abandon'),
    [singleDateData]);
  const { data: sdAsaData, campaigns: sdAsaCampaigns } = useMemo(
    () => buildHourChartData(singleDateData?.asa_by_hour ?? [], 'ASA', 'asa'),
    [singleDateData]);
  const { data: sdSlaData, campaigns: sdSlaCampaigns } = useMemo(
    () => buildSlaHourChartData(singleDateData?.sla_by_hour ?? []),
    [singleDateData]);
  const { pivotRows: sdCampRows, activeHours: sdCampHours, campaignSpans: sdCampSpans } = useMemo(
    () => buildHourPivotGroups(
      singleDateData?.campaign_hour_rows ?? [],
      (r) => r.Campaign,
      (r) => ({ _campaign: r.Campaign }),
    ), [singleDateData]);

  const sdAvgHourMap = useMemo(() => {
    if (!sdLookbackData?.length) return null;
    const m = {};
    sdLookbackData.forEach(r => { m[r.Hour] = r; });
    return m;
  }, [sdLookbackData]);

  const sdOfferedChartData = useMemo(() => {
    if (!sdAvgHourMap || sdHideAvg) return { data: sdOfferedData, hasAvg: false };
    return {
      data: sdOfferedData.map(d => ({ ...d, _dow_avg: sdAvgHourMap[d.hour]?.AvgOffered ?? null })),
      hasAvg: true,
    };
  }, [sdOfferedData, sdAvgHourMap, sdHideAvg]);

  const sdAbandonChartData = useMemo(() => {
    if (!sdAvgHourMap || sdHideAvg) return { data: sdAbandonData, hasAvg: false };
    return {
      data: sdAbandonData.map(d => ({ ...d, _dow_avg: sdAvgHourMap[d.hour]?.AvgAbandon ?? null })),
      hasAvg: true,
    };
  }, [sdAbandonData, sdAvgHourMap, sdHideAvg]);

  const sdAsaChartData = useMemo(() => {
    if (!sdAvgHourMap || sdHideAvg) return { data: sdAsaData, hasAvg: false };
    return {
      data: sdAsaData.map(d => ({ ...d, _dow_avg: sdAvgHourMap[d.hour]?.AvgASA ?? null })),
      hasAvg: true,
    };
  }, [sdAsaData, sdAvgHourMap, sdHideAvg]);

  const sdSlaChartData = useMemo(() => {
    if (!sdAvgHourMap || sdHideAvg) return { data: sdSlaData, hasAvg: false };
    return {
      data: sdSlaData.map(d => ({ ...d, _dow_avg: sdAvgHourMap[d.hour]?.AvgRawSLA != null ? +Number(sdAvgHourMap[d.hour].AvgRawSLA).toFixed(1) : null })),
      hasAvg: true,
    };
  }, [sdSlaData, sdAvgHourMap, sdHideAvg]);

  const sdAsaTotalsOverride = useMemo(() => {
    const rows = singleDateData?.asa_by_hour ?? [];
    if (!rows.length) return undefined;
    return sdAsaCampaigns.map((c, i) => {
      const campRows = rows.filter((r) => r.Campaign === c);
      const totalHandled = campRows.reduce((a, r) => a + (r.Handled ?? 0), 0);
      const totalInqueue = campRows.reduce((a, r) => a + (r.HandledInqueueTime ?? 0), 0);
      const avg = totalHandled > 0 ? totalInqueue / totalHandled : null;
      return { c, i, display: fmtMmSs(avg) };
    });
  }, [singleDateData, sdAsaCampaigns]);

  const sdSlaTotalsOverride = useMemo(() => {
    const rows = singleDateData?.sla_by_hour ?? [];
    if (!rows.length) return undefined;
    return sdSlaCampaigns.map((c, i) => {
      const campRows = rows.filter((r) => r.Campaign === c);
      const inSLA    = campRows.reduce((a, r) => a + (r.HandledInSLA ?? 0), 0);
      const eligible = campRows.reduce((a, r) => a + (r.EligibleCalls ?? 0), 0);
      const pct = eligible > 0 ? inSLA / eligible * 100 : null;
      return { c, i, display: pct != null ? `${fmt(pct, 1)}%` : '—' };
    });
  }, [singleDateData, sdSlaCampaigns]);


  // Embedded: the filters come from the page above, so the search runs
  // whenever they change rather than when a button is pressed.
  //
  // Single Date is the exception. It asks about one day, and the page above
  // supplies a range, so standalone it switches the filter panel to a single
  // date picker -- which is hidden here. Rather than teach the shell what each
  // tab's internals want, the view carries its own picker and narrows the
  // range to the chosen day. It defaults to the end of the range, which is the
  // most recent day the window covers.
  const [embeddedDate, setEmbeddedDate] = useState(null);

  // Yesterday, not the end of the range. A range that runs to today ends on a
  // day whose calls are still arriving, so defaulting to it shows an empty or
  // half-finished table. This matches what the standalone page does when the
  // view is switched.
  //
  // Capped at the range end so a window that closed weeks ago still opens on a
  // date inside itself rather than on a day it does not cover.
  const defaultSingleDate = useMemo(() => {
    const yesterday = dayjs().subtract(1, 'day');
    const end = embeddedFilters?.end_date ? dayjs(embeddedFilters.end_date) : yesterday;
    return end.isAfter(yesterday, 'day') ? yesterday : end;
  }, [embeddedFilters]);

  // By Month needs a long window to be a trend at all, and the page's range
  // defaults to about six weeks -- so it would have been six weeks of monthly
  // rows, and every visit would start by widening the picker. It takes its own
  // six months instead.
  //
  // The range picker above still shows, because the other views do use it.
  // The label below says when it is not being applied.
  const monthlyWindow = useMemo(() => ({
    start_date: dayjs().subtract(5, 'month').startOf('month').format('YYYY-MM-DD'),
    end_date:   dayjs().format('YYYY-MM-DD'),
  }), []);

  const embeddedEffective = useMemo(() => {
    if (!embedded || !embeddedFilters) return null;
    if (view === 'monthly') return { ...embeddedFilters, ...monthlyWindow };
    if (view !== 'single-date') return embeddedFilters;
    const d = (embeddedDate ?? defaultSingleDate).format('YYYY-MM-DD');
    return { ...embeddedFilters, start_date: d, end_date: d };
  }, [embedded, embeddedFilters, view, embeddedDate, defaultSingleDate, monthlyWindow]);

  // Keyed on the serialised filters because the object identity changes every
  // render. Switching into or out of Single Date changes the dates, so the key
  // changes and the refetch happens; switching between the other views does
  // not, and none is needed because they read the same six responses.
  const embeddedKey = embeddedEffective ? JSON.stringify(embeddedEffective) : null;
  useEffect(() => {
    if (!embeddedEffective) return;
    handleSearch(embeddedEffective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedKey]);

  return (
    <div>
      {!embedded && (
        <FilterPanel
          ref={filterRef}
          sessionKey="campaign-stats"
          onSearch={handleSearch}
          loading={loading}
          hideAIFilters
          hiddenFilters={HIDDEN_FILTERS}
          optionsEndpoint="/api/campaign-stats/options"
          defaultDateRange={SHARED_DEFAULT_RANGE()}
          singleDate={view === 'single-date'}
          requireCampaign
          singleCampaign
        />
      )}

      {embedded && embeddedEffective && (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
          {embeddedEffective.start_date} to {embeddedEffective.end_date}
          {view === 'monthly' && ' \u00b7 By Month always covers six months'}
        </Typography.Text>
      )}

      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={view}
          onChange={handleViewChange}
          options={[
            { label: 'Summary',        value: 'summary'      },
            { label: 'By Week',        value: 'by-week'      },
            { label: 'By Day of Week', value: 'by-dow'       },
            { label: 'By Day',         value: 'by-day'       },
            { label: 'By Hour',        value: 'by-hour'      },
            { label: 'Single Date',    value: 'single-date'  },
            { label: 'By Month',       value: 'monthly'      },
          ]}
        />
      </div>

      <Spin spinning={loading}>
        {/* ── Single Date view ── */}
        {view === 'single-date' && (
          <>
            <Space wrap style={{ marginBottom: 16 }}>
              {embedded && (
                <>
                  <Typography.Text style={{ fontSize: 13 }}>Date:</Typography.Text>
                  <DatePicker
                    size="small"
                    allowClear={false}
                    style={{ width: 140 }}
                    value={embeddedDate ?? defaultSingleDate}
                    onChange={(d) => d && setEmbeddedDate(d)}
                  />
                </>
              )}
              <AntTooltip title="For this same Day of Week, how many weeks to look back for the average">
                <Typography.Text style={{ fontSize: 13 }}>DOW Lookback (wks):</Typography.Text>
              </AntTooltip>
              <Select
                size="small"
                style={{ width: 70 }}
                value={sdLookbackWeeks}
                onChange={handleSdLookbackWeeksChange}
                options={LOOKBACK_OPTIONS}
              />
              <Checkbox checked={sdHideAvg} onChange={(e) => setSdHideAvg(e.target.checked)}>
                Hide averages
              </Checkbox>
            </Space>
            <Spin spinning={sdLoading}>
              <SDHourBarChart
                title="Raw SLA Inbound Campaign By Hour"
                data={sdSlaChartData.data}
                campaigns={sdSlaCampaigns}
                metricKey="raw"
                labelFmt={(v) => v != null ? `${fmt(v, 1)}%` : ''}
                yFmt={(v) => `${v}%`}
                hasAvg={sdSlaChartData.hasAvg}
                avgLabel={`DOW Avg (${sdLookbackWeeks} wks)`}
                alertLower
                totalsOverride={sdSlaTotalsOverride}
              />
              <SDHourBarChart
                title="Offered Inbound Campaign By Hour"
                data={sdOfferedChartData.data}
                campaigns={sdOfferedCampaigns}
                metricKey="offered"
                hasAvg={sdOfferedChartData.hasAvg}
                avgLabel={`DOW Avg (${sdLookbackWeeks} wks)`}
                alertHigher
              />
              <SDHourBarChart
                title="Abandon Inbound Campaign By Hour"
                data={sdAbandonChartData.data}
                campaigns={sdAbandonCampaigns}
                metricKey="abandon"
                hasAvg={sdAbandonChartData.hasAvg}
                avgLabel={`DOW Avg (${sdLookbackWeeks} wks)`}
                alertHigher
              />
              <SDHourBarChart
                title="ASA Inbound Campaign By Hour"
                data={sdAsaChartData.data}
                campaigns={sdAsaCampaigns}
                metricKey="asa"
                labelFmt={fmtMmSs}
                yFmt={fmtMmSs}
                hasAvg={sdAsaChartData.hasAvg}
                avgLabel={`DOW Avg (${sdLookbackWeeks} wks)`}
                totalsOverride={sdAsaTotalsOverride}
              />
              <SDHourPivotTable
                title="Campaign By Hour"
                pivotRows={sdCampRows}
                activeHours={sdCampHours}
                campaignSpans={sdCampSpans}
                fixedCols={[]}
                loading={sdLoading}
              />
              {!sdLoading && singleDateData === null && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a campaign and click Search, then pick a date." />
              )}
            </Spin>
          </>
        )}

        {/* ── Period views ── */}
        {view === 'monthly'  && <PeriodView data={monthlyData} periodTitle="Call Period"  summary={summary} reverseForChart />}
        {view === 'by-week'  && <PeriodView data={byWeekData}  periodTitle="Week"         summary={summary} reverseForChart />}
        {view === 'by-dow'   && <PeriodView data={byDowData}   periodTitle="Day of Week"  summary={summary} />}
        {view === 'by-dow'   && <DowDatesCharts daily={daily} />}
        {view === 'by-day'   && <PeriodView data={dailyData}   periodTitle="Date"         summary={summary} reverseForChart />}
        {view === 'by-hour'  && <PeriodView data={byHourData}  periodTitle="Hour"         summary={summary} />}

        {/* ── Summary view ── */}
        {view === 'summary' && (
          <>
            {/* Row 1: volume metrics */}
            <Row gutter={[10, 10]} style={{ marginTop: 16 }}>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Abandon"          value={s.Abandon} />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Short Abandon"    value={s.ShortAbandon} />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Raw Abandon Rate" value={s.RawAbandonRate} suffix="%" decimals={1} />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Offered"          value={s.Offered} />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Handled"          value={s.Handled} />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Time on Hold %"   value={s.TimeOnHoldPct} suffix="%" decimals={1} />
              </Col>
            </Row>

            {/* Row 2: time / quality metrics */}
            <Row gutter={[10, 10]} style={{ marginTop: 10 }}>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Raw ASA (mm:ss)"      value={s.RawASA}        mmss />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Raw SLA"              value={s.RawSLA}        suffix="%" decimals={1} />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Customer SLA"         value={s.CustomerSLA}   suffix="%" decimals={1} />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Raw AHT (mm:ss)"      value={s.RawAHT}        mmss />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Raw AvgTalk (mm:ss)"  value={s.RawAvgTalk}    mmss />
              </Col>
              <Col xs={12} sm={6} md={4} lg={3}>
                <KpiCard title="Raw ACW (mm:ss)"      value={s.RawACW}        mmss />
              </Col>
            </Row>

            {/* Charts row 1 */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} lg={12}>
                <Card title="Offered vs Abandon">
                  {dailyChart.length === 0
                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Run a search to see data" />
                    : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dailyChart} margin={{ top: 20, right: 16, bottom: 8, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="Day" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v, name) => [v.toLocaleString(), name]} />
                          <Legend />
                          <Bar dataKey="Offered" name="Offered" fill="#1677ff">
                            <LabelList dataKey="Offered" position="top" style={{ fontSize: 10 }} formatter={(v) => v.toLocaleString()} />
                          </Bar>
                          <Bar dataKey="Abandon" name="Abandon" fill="#13c2c2">
                            <LabelList dataKey="Abandon" position="top" style={{ fontSize: 10 }} formatter={(v) => v.toLocaleString()} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card title="Raw SLA">
                  {dailyChart.length === 0
                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Run a search to see data" />
                    : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dailyChart} margin={{ top: 20, right: 16, bottom: 8, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="Day" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(v) => `${v}%`} />
                          <Tooltip formatter={(v, name) => [`${fmt(v, 1)}%`, name]} />
                          <Bar dataKey="RawSLAPct" name="Raw SLA" fill="#13c2c2">
                            <LabelList content={<PCT_LABEL />} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </Card>
              </Col>
            </Row>

            {/* Charts row 2 */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} lg={12}>
                <Card title="Abandon Rate">
                  {dailyChart.length === 0
                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Run a search to see data" />
                    : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dailyChart} margin={{ top: 20, right: 16, bottom: 8, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="Day" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(v) => `${v}%`} />
                          <Tooltip formatter={(v) => [`${fmt(v, 1)}%`, 'Raw Abandon Rate']} />
                          <Bar dataKey="AbandonRatePct" name="Raw Abandon Rate" fill="#1677ff">
                            <LabelList content={<PCT_LABEL />} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card title="Handle Time Breakdown">
                  {dailyChart.length === 0
                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Run a search to see data" />
                    : (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dailyChart} margin={{ top: 20, right: 16, bottom: 8, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="Day" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={fmtMmSs} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v, name) => [fmtMmSs(v), name]} />
                          <Legend />
                          <Line type="monotone" dataKey="RawAvgTalk" name="Agent Time" stroke="#1677ff" dot={false} strokeWidth={2} />
                          <Line type="monotone" dataKey="RawACW"     name="ACW"        stroke="#fa8c16" dot={false} strokeWidth={2} />
                          <Line type="monotone" dataKey="RawAHT"     name="AHT"        stroke="#52c41a" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Spin>
    </div>
  );
}
